import { useState } from 'react';
import { X, Shield, AlertCircle } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';
import { AvaAccessToggle } from '../ava/AvaAccessToggle';

interface AdminForPermissions {
  _id: string;
  email: string;
  profile?: { firstName?: string; lastName?: string };
  adminData?: {
    canIssueRefunds?: boolean;
    canRecordPayment?: boolean;
    canMoveHierarchy?: boolean;
    canCompOrders?: boolean;
    canManageTelemedNetworks?: boolean;
  };
  avaEnabled?: boolean;
}

interface Props {
  admin: AdminForPermissions;
  isSelf: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminPermissionsModal({ admin, isSelf, onClose, onSuccess }: Props) {
  const [canIssueRefunds, setCanIssueRefunds] = useState<boolean>(
    admin.adminData?.canIssueRefunds ?? false
  );
  const [canRecordPayment, setCanRecordPayment] = useState<boolean>(
    admin.adminData?.canRecordPayment ?? false
  );
  const [canMoveHierarchy, setCanMoveHierarchy] = useState<boolean>(
    admin.adminData?.canMoveHierarchy ?? false
  );
  const [canCompOrders, setCanCompOrders] = useState<boolean>(
    admin.adminData?.canCompOrders ?? false
  );
  const [canManageTelemedNetworks, setCanManageTelemedNetworks] = useState<boolean>(
    admin.adminData?.canManageTelemedNetworks ?? false
  );
  const [submitting, setSubmitting] = useState(false);

  const initialRefunds = admin.adminData?.canIssueRefunds ?? false;
  const initialRecord = admin.adminData?.canRecordPayment ?? false;
  const initialMove = admin.adminData?.canMoveHierarchy ?? false;
  const initialComp = admin.adminData?.canCompOrders ?? false;
  const initialTelemed = admin.adminData?.canManageTelemedNetworks ?? false;
  const dirty =
    canIssueRefunds !== initialRefunds ||
    canRecordPayment !== initialRecord ||
    canMoveHierarchy !== initialMove ||
    canCompOrders !== initialComp ||
    canManageTelemedNetworks !== initialTelemed;

  const handleSubmit = async () => {
    if (!dirty) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.updateAdminPermissions(admin._id, {
        canIssueRefunds,
        canRecordPayment,
        canMoveHierarchy,
        canCompOrders,
        canManageTelemedNetworks
      });
      if (res.success) {
        toast.success('Permissions updated');
        onSuccess();
        onClose();
      } else {
        const msg =
          res.code === 'self_permission_edit'
            ? "You can't edit your own permissions."
            : res.error || 'Failed to update permissions';
        toast.error(msg);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      toast.error(data?.error || err?.message || 'Failed to update permissions');
    } finally {
      setSubmitting(false);
    }
  };

  const displayName = [admin.profile?.firstName, admin.profile?.lastName]
    .filter(Boolean)
    .join(' ') || admin.email;

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]"
      onClick={onClose}
    >
      <div
        className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Admin permissions</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="text-sm text-gray-600 mb-4">{displayName}</div>

        {isSelf && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 flex gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              You can't edit your own permissions. Ask another admin if you need a change.
            </div>
          </div>
        )}

        <div className="space-y-3">
          <label className={`flex items-start gap-3 p-3 rounded-md border ${isSelf ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={canIssueRefunds}
              onChange={e => setCanIssueRefunds(e.target.checked)}
              disabled={isSelf || submitting}
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium text-gray-900">Can issue invoice refunds</div>
              <div className="text-gray-600">
                Lets this admin refund paid invoices through the payment processor and auto-issue clawback credit memos.
              </div>
            </div>
          </label>

          <label className={`flex items-start gap-3 p-3 rounded-md border ${isSelf ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={canRecordPayment}
              onChange={e => setCanRecordPayment(e.target.checked)}
              disabled={isSelf || submitting}
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium text-gray-900">Can record manual payment</div>
              <div className="text-gray-600">
                Lets this admin mark an invoice paid outside the payment processor (cash, check, wire).
              </div>
            </div>
          </label>

          <label className={`flex items-start gap-3 p-3 rounded-md border ${isSelf ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={canMoveHierarchy}
              onChange={e => setCanMoveHierarchy(e.target.checked)}
              disabled={isSelf || submitting}
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium text-gray-900">Can move accounts in the hierarchy</div>
              <div className="text-gray-600">
                Lets this admin move physicians between AMs and AMs between supervisors. Catalogs are preserved on move; past invoices stay with the prior chain.
              </div>
            </div>
          </label>

          <label className={`flex items-start gap-3 p-3 rounded-md border ${isSelf ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={canCompOrders}
              onChange={e => setCanCompOrders(e.target.checked)}
              disabled={isSelf || submitting}
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium text-gray-900">Can comp bulk orders</div>
              <div className="text-gray-600">
                Lets this admin create a bulk order at $0 — all item prices, markups, and shipping zeroed; a paid $0 invoice generates; no payouts attach. Requires a reason on every comp.
              </div>
            </div>
          </label>

          <label className={`flex items-start gap-3 p-3 rounded-md border ${isSelf ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}>
            <input
              type="checkbox"
              checked={canManageTelemedNetworks}
              onChange={e => setCanManageTelemedNetworks(e.target.checked)}
              disabled={isSelf || submitting}
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium text-gray-900">Can manage telemed networks</div>
              <div className="text-gray-600">
                Unlocks the telemed network feature for this admin: create networks and rosters, assign a network to clinics, and flag products as requiring it. Admins without this bit don't see the feature at all.
              </div>
            </div>
          </label>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-md border p-3">
          <div className="text-sm">
            <div className="font-medium text-gray-900">Ava assistant</div>
            <div className="text-gray-600">Saves immediately. Off for everyone by default.</div>
          </div>
          <AvaAccessToggle userId={admin._id} enabled={admin.avaEnabled !== false} onChange={() => onSuccess()} />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSelf || submitting || !dirty}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
